/**
 * @description Hook utilizado para manipular os usuários
 * @author Diego Sabino Amorim de Araújo
 * @file useUsuarioService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import constants from 'utils/constants'
import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { useFetch } from './useFetch'

export const useUsuarioService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarUsuario = async (id, setUsuario, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/usuarios/${id}`)
      setUsuario(response.data.usuario)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarUsuarioAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/usuarios/${id}`)
      return response.data.usuario
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarUsuarioExterno = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/usuarios/externos/${id}/`)
      return response.data.usuario
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  /* Lista todos os usuários menos os usuários FAKE */
  const listarUsuarios = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`usuarios/usuariosUteis`)
      dispatch({
        type: actionTypes.OPCOES_USUARIOS,
        usuarios: response.data.usuarios,
      })
      return response.data.usuarios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  /* Lista todos os usuários menos os usuários FAKE */
  const listarUsuariosAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`usuarios/usuariosUteis`)
      return response.data.usuarios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTodosOsUsuarios = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`usuarios`)
      dispatch({
        type: actionTypes.OPCOES_USUARIOS,
        usuarios: response.data.usuarios,
      })
      return response.data.usuarios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarUsuariosExternos = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`usuarios/externos`)
      return response.data.usuarios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExaminadoresCandidatos = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/usuarios/exameSolicitado/examinadoresCandidatos`)
      dispatch({
        type: actionTypes.FETCH_EXAMINADORES_CADIDATOS,
        usuariosCandidatos: response.data.usuarios,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const deletarUsuario = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`/usuarios/${id}/`)
    if (setProcessando) setProcessando(false)
  }

  const cadastrarUsuario = async (dados, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const novoUsuario = {
      nome: dados.nome ? dados.nome : null,
      cpf: dados.cpf ? dados.cpf : null,
      email: dados.email ? dados.email : null,
      matricula: dados.matricula ? dados.matricula : null,
      cargo: dados.cargo ? dados.cargo.id : null,
      setores: dados.setores ? dados.setores.map((setor) => setor.id) : null,
      permissoesAdicionais: dados.permissoesAdicionais ? dados.permissoesAdicionais.map((permAdic) => permAdic.id) : null,
      permissoesNegadas: dados.permissoesNegadas ? dados.permissoesNegadas.map((permNeg) => permNeg.id) : null,
      notificacao: dados.notificacao ? dados.notificacao : null,
    }
    await fetch.postOpen('/usuarios/cadastrar/', novoUsuario)
    if (setProcessando) setProcessando(false)
  }

  const editarUsuario = async (id, dados, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const usuario = {
      nome: dados.nome ? dados.nome : null,
      cpf: dados.cpf ? dados.cpf : null,
      matricula: dados.matricula ? dados.matricula : null,
      email: dados.email ? dados.email : null,
      cargo: dados.cargo ? dados.cargo.id : null,
      setores: dados.setores ? dados.setores.map((setor) => setor.id) : null,
      permissoesAdicionais: dados.permissoesAdicionais ? dados.permissoesAdicionais.map((permAdic) => permAdic.id) : null,
      permissoesNegadas: dados.permissoesNegadas ? dados.permissoesNegadas.map((permNeg) => permNeg.id) : null,
    }
    await fetch.putOpen(`/usuarios/${id}/`, usuario)
    if (setProcessando) setProcessando(false)
  }

  const setPermissaoAcoesExaminador = (exameSolicitado, usuario) => {
    let usuarioPermitido = false
    let setorUsuarioPermito = false
    let usuarioAtribuidoAoExame = false
    for (let i = 0; i < usuario.cargo.perms.length; i += 1) {
      if (usuario.cargo.perms[i].target === constants.PERMISSOES.REALIZAR_EXAME) {
        usuarioPermitido = true
        break
      }
    }

    for (let i = 0; i < usuario.permAdics.length; i += 1) {
      if (usuario.permAdics[i].target === constants.PERMISSOES.REALIZAR_EXAME) {
        usuarioPermitido = true
        break
      }
    }

    for (let i = 0; i < usuario.permNegs.length; i += 1) {
      if (usuario.permNegs[i].target === constants.PERMISSOES.REALIZAR_EXAME) {
        usuarioPermitido = false
        break
      }
    }

    for (let i = 0; i < usuario.setores.length; i += 1) {
      if (usuario.setores[i].id === exameSolicitado.setor.id) {
        setorUsuarioPermito = true
      }
    }

    if (exameSolicitado.exReal) {
      if (exameSolicitado.exReal.usuExams) {
        exameSolicitado.exReal.usuExams.forEach((examinadorAtribuido) => {
          if (examinadorAtribuido.id === usuario.id) {
            usuarioAtribuidoAoExame = true
          }
        })
      }
    }

    dispatch({
      type: actionTypes.SET_PERMISSAO_ACOES_EXAMINADOR,
      permissaoAcoesExaminador: usuarioPermitido && setorUsuarioPermito && usuarioAtribuidoAoExame,
    })

    dispatch({
      type: actionTypes.SET_PERMISSAO_ASSUMIR_EXAME,
      permissaoAssumirExame: usuarioPermitido && setorUsuarioPermito,
    })
  }

  const listarAutoridadesRequisitantes = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/usuarios/autoridadesRequisitantes/comSetores`)
      dispatch({
        type: actionTypes.AUTORIDADES_REQUISITANTES,
        autoridadesRequisitantes: response.data.usuarios,
      })
      return response.data.usuarios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarUsuariosDeSetorAsync = async (idSetor, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/usuarios/deSetor/${idSetor}`)
      return response.data.usuarios
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarPreferenciasDashboardAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/usuarios/preferencias/dashboard`)
      return response.data.preferenciasDashboard
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarPreferenciasDashboard = async (values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.postOpen(`/usuarios/preferencias/dashboard`, values)
      return response.data.preferenciasDashboard
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarPreferenciasDashboard = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.putOpen(`/usuarios/preferencias/dashboard/${id}`, values)
      return response.data.preferenciasDashboard
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const verificarUsuarioComDadosRegistrados = async (cpf, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.postOpen(`/auth/verificarDadosRegistrados/${cpf}`)
      return response?.data?.dadosRegistrados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const aceitarTermoConfidencialidade = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen('/usuarios/termoconfidencialidade/aceitar')
    } catch (error) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }
  return {
    listarUsuario,
    listarUsuarioAsync,
    listarExaminadoresCandidatos,
    setPermissaoAcoesExaminador,
    listarAutoridadesRequisitantes,
    listarUsuarios,
    listarUsuariosAsync,
    deletarUsuario,
    cadastrarUsuario,
    editarUsuario,
    listarUsuariosDeSetorAsync,
    listarPreferenciasDashboardAsync,
    cadastrarPreferenciasDashboard,
    editarPreferenciasDashboard,
    verificarUsuarioComDadosRegistrados,
    aceitarTermoConfidencialidade,
    listarTodosOsUsuarios,
    listarUsuariosExternos,
    listarUsuarioExterno,
  }
}
